import { defineStore } from "pinia";

/**
 * Report Filter Store
 *
 * Centralized store for managing report filter state across components.
 * This eliminates the need for complex event propagation and provides
 * a single source of truth for all filter-related state.
 */
export const useReportFilterStore = defineStore("reportFilter", {
  state: () => ({
    // Current account being filtered (to scope filters per account)
    currentAccountId: null,

    // Filter state
    filters: {
      // Date filter
      day_eq: "",

      // User filter
      user_id_eq: "",

      // Category filter
      report_category_name_in: [],

      // Text search
      description_cont: "",

      // Important filter
      important_eq: "",

      // Trait filters (cache-based)
      trait_ids_cache_cont_any: [], // Array of ",id," strings for "any" mode
      trait_ids_cache_cont: "", // Combined ",id1,id2," string for "all" mode

      // Contact filters (mention-based, cached like traits)
      contact_ids_cache_cont_any: [], // Array of ",id," strings for "any" mode (OR logic)
    },

    // Filter dialog state
    dialog: {
      show: false,
    },

    // Trait filter mode (true = any, false = all)
    traitsAnyMode: true,
  }),

  getters: {
    // Get currently selected trait IDs (regardless of mode)
    selectedTraitIds: (state) => {
      if (
        state.traitsAnyMode &&
        Array.isArray(state.filters.trait_ids_cache_cont_any)
      ) {
        // Extract IDs from ",id," format
        return state.filters.trait_ids_cache_cont_any
          .map((s) => (typeof s === "string" ? s.replace(/,/g, "") : ""))
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n));
      } else if (
        !state.traitsAnyMode &&
        typeof state.filters.trait_ids_cache_cont === "string"
      ) {
        // Extract IDs from ",id1,id2," format
        return state.filters.trait_ids_cache_cont
          .split(",")
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n));
      }
      return [];
    },

    // Check if a specific trait ID is selected
    isTraitSelected: (state) => (traitId) => {
      const selectedIds = state.selectedTraitIds || [];
      return selectedIds.includes(Number(traitId));
    },

    // Get currently selected contact IDs
    selectedContactIds: (state) => {
      if (Array.isArray(state.filters.contact_ids_cache_cont_any)) {
        // Extract IDs from ",id," format (same as traits)
        return state.filters.contact_ids_cache_cont_any
          .map((s) => (typeof s === "string" ? s.replace(/,/g, "") : ""))
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n));
      }
      return [];
    },

    // Check if a specific contact ID is selected
    isContactSelected: (state) => (contactId) => {
      const selectedIds = state.selectedContactIds || [];
      return selectedIds.includes(Number(contactId));
    },

    // Count total active filters (for badge display)
    activeFiltersCount: (state) => {
      let count = 0;

      if (state.filters.day_eq) count++;
      if (state.filters.user_id_eq) count++;
      if (state.filters.description_cont) count++;
      if (
        state.filters.important_eq !== "" &&
        state.filters.important_eq !== null
      )
        count++;
      if (
        Array.isArray(state.filters.report_category_name_in) &&
        state.filters.report_category_name_in.length > 0
      )
        count++;

      // Count trait filters
      if (state.traitsAnyMode) {
        if (
          Array.isArray(state.filters.trait_ids_cache_cont_any) &&
          state.filters.trait_ids_cache_cont_any.length > 0
        )
          count++;
      } else {
        if (
          typeof state.filters.trait_ids_cache_cont === "string" &&
          state.filters.trait_ids_cache_cont.length > 0
        )
          count++;
      }

      // Count contact filters
      if (
        Array.isArray(state.filters.contact_ids_cache_cont_any) &&
        state.filters.contact_ids_cache_cont_any.length > 0
      )
        count++;

      return count;
    },

    // Check if any filters are active
    hasActiveFilters: (state) => {
      return state.activeFiltersCount > 0;
    },

    // Get search query object for API calls
    searchQuery: (state) => {
      const query = {
        account_id_eq: state.currentAccountId,
      };

      // Add non-empty filters
      if (state.filters.day_eq) query.day_eq = state.filters.day_eq;
      if (state.filters.user_id_eq) query.user_id_eq = state.filters.user_id_eq;
      if (state.filters.description_cont)
        query.description_cont = state.filters.description_cont;
      if (
        state.filters.important_eq !== "" &&
        state.filters.important_eq !== null
      ) {
        query.important_eq = state.filters.important_eq;
      }
      if (
        Array.isArray(state.filters.report_category_name_in) &&
        state.filters.report_category_name_in.length > 0
      ) {
        query.report_category_name_in = state.filters.report_category_name_in;
      }

      // Add trait filters
      if (
        Array.isArray(state.filters.trait_ids_cache_cont_any) &&
        state.filters.trait_ids_cache_cont_any.length > 0
      ) {
        query.trait_ids_cache_cont_any = state.filters.trait_ids_cache_cont_any;
      }
      if (state.filters.trait_ids_cache_cont) {
        query.trait_ids_cache_cont = state.filters.trait_ids_cache_cont;
      }

      // Add contact filters (same format as traits)
      if (
        Array.isArray(state.filters.contact_ids_cache_cont_any) &&
        state.filters.contact_ids_cache_cont_any.length > 0
      ) {
        query.contact_ids_cache_cont_any = state.filters.contact_ids_cache_cont_any;
        console.log("ReportFilterStore: searchQuery includes contact filter:", query.contact_ids_cache_cont_any);
      }

      return query;
    },
  },

  actions: {
    // Initialize for a specific account
    initForAccount(accountId) {
      this.currentAccountId = accountId;
      this.clearAllFilters();
    },

    // Set text search filter
    setTextSearch(searchText) {
      this.filters.description_cont = searchText || "";
    },

    // Clear all filters
    clearAllFilters() {
      this.filters = {
        day_eq: "",
        user_id_eq: "",
        report_category_name_in: [],
        description_cont: "",
        important_eq: "",
        trait_ids_cache_cont_any: [],
        trait_ids_cache_cont: "",
        contact_ids_cache_cont_any: [],
      };
      this.traitsAnyMode = true;
    },

    // Toggle trait selection (for clickable trait chips)
    toggleTrait(traitId) {
      const numericId = Number(traitId);
      if (!numericId || numericId <= 0) return;

      this.traitsAnyMode = true;
      this.filters.trait_ids_cache_cont = "";

      if (!Array.isArray(this.filters.trait_ids_cache_cont_any)) {
        this.filters.trait_ids_cache_cont_any = [];
      }

      const cacheString = `,${numericId},`;
      const index = this.filters.trait_ids_cache_cont_any.indexOf(cacheString);

      if (index === -1) {
        this.filters.trait_ids_cache_cont_any.push(cacheString);
      } else {
        this.filters.trait_ids_cache_cont_any.splice(index, 1);
      }
    },

    // Toggle contact selection (for clickable contact chips)
    // Uses same format as traits: ",id," cache strings
    toggleContact(contactId) {
      const numericId = Number(contactId);
      if (!numericId || numericId <= 0) return;

      if (!Array.isArray(this.filters.contact_ids_cache_cont_any)) {
        this.filters.contact_ids_cache_cont_any = [];
      }

      const cacheString = `,${numericId},`;
      const index = this.filters.contact_ids_cache_cont_any.indexOf(cacheString);

      if (index === -1) {
        // Add contact to filter (OR logic)
        // Create new array to ensure Vue reactivity
        this.filters.contact_ids_cache_cont_any = [...this.filters.contact_ids_cache_cont_any, cacheString];
        console.log("ReportFilterStore: Added contact filter:", cacheString, "Filters:", this.filters.contact_ids_cache_cont_any);
      } else {
        // Remove contact from filter
        // Create new array to ensure Vue reactivity
        this.filters.contact_ids_cache_cont_any = this.filters.contact_ids_cache_cont_any.filter((item, i) => i !== index);
        console.log("ReportFilterStore: Removed contact filter:", cacheString, "Filters:", this.filters.contact_ids_cache_cont_any);
      }
    },

    // Dialog actions
    openDialog() {
      this.dialog.show = true;
    },

    closeDialog() {
      this.dialog.show = false;
    },

    // Apply filters from dialog (called on submit)
    applyFilters(filters, traitsAnyMode) {
      this.filters = { ...filters };
      this.traitsAnyMode = traitsAnyMode;
    },

    // Apply a preset filter (like clicking a trait chip)
    applyTraitFilter(traitId) {
      this.clearAllFilters();
      this.toggleTrait(traitId);
    },
  },
});
